const VIDEO_CAPTION_VALIDATION_TIMEOUT_MS = 5_000;

const hasUsableCaptionCues = (track: HTMLTrackElement): boolean => {
  const cues = Array.from(track.track.cues ?? []);
  return (
    cues.length > 0 &&
    cues.every((cue) => {
      const { startTime, endTime } = cue;
      const text = (cue as VTTCue).text;
      return (
        Number.isFinite(startTime) &&
        startTime >= 0 &&
        Number.isFinite(endTime) &&
        endTime > startTime &&
        typeof text === "string" &&
        text.trim().length > 0
      );
    })
  );
};

/**
 * Uses the browser's native WebVTT parser to validate a local caption file.
 * The temporary track is hidden, so cues are parsed without being displayed. A
 * file is usable only when the parser produces at least one well-formed cue.
 */
export const isValidVideoCaptionFile = (file: Blob): Promise<boolean> =>
  new Promise((resolve) => {
    const video = document.createElement("video");
    const track = document.createElement("track");
    let objectUrl: string | undefined;
    let timeoutId: number | undefined;
    let settled = false;

    const finish = (isValid: boolean) => {
      if (settled) return;
      settled = true;
      if (timeoutId !== undefined) window.clearTimeout(timeoutId);
      track.removeEventListener("load", handleLoad);
      track.removeEventListener("error", handleError);
      video.remove();
      if (objectUrl) URL.revokeObjectURL(objectUrl);
      resolve(isValid);
    };
    const handleLoad = () => finish(hasUsableCaptionCues(track));
    const handleError = () => finish(false);

    try {
      // finish() revokes this URL after load, error, timeout, or setup failure.
      // react-doctor-disable-next-line react-doctor/no-create-object-url-without-revoke
      objectUrl = URL.createObjectURL(new Blob([file], { type: "text/vtt" }));
      track.kind = "captions";
      track.srclang = "en";
      track.track.mode = "hidden";
      track.addEventListener("load", handleLoad);
      track.addEventListener("error", handleError);
      video.hidden = true;
      video.append(track);
      document.body.append(video);
      timeoutId = window.setTimeout(() => finish(false), VIDEO_CAPTION_VALIDATION_TIMEOUT_MS);
      track.src = objectUrl;
    } catch {
      finish(false);
    }
  });
